import type { JournalEntry } from '../types/journal';

const CSV_HEADER =
  'Date,Serving Size,Cannabinoid Type,Cannabinoid (mg),Recipe,Onset (min),Peak (min),Duration (min),Effects,Notes,Rating';

/**
 * Escapes a CSV field per RFC 4180.
 * If the field contains a comma, double quote, or newline, it is wrapped in
 * double quotes and any internal double quotes are doubled.
 */
function escapeCsvField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Formats a number with up to 2 decimal places (no trailing zeros).
 */
function formatNumber(value: number): string {
  return parseFloat(value.toFixed(2)).toString();
}

/**
 * Converts a JournalEntry to a CSV row string.
 */
function entryToRow(entry: JournalEntry): string {
  const fields: string[] = [
    escapeCsvField(entry.consumedAt),
    formatNumber(entry.servingSize),
    escapeCsvField(entry.cannabinoidType),
    formatNumber(entry.cannabinoidMg),
    escapeCsvField(entry.recipeName ?? ''),
    entry.onsetMinutes != null ? formatNumber(entry.onsetMinutes) : '',
    entry.peakMinutes != null ? formatNumber(entry.peakMinutes) : '',
    entry.durationMinutes != null ? formatNumber(entry.durationMinutes) : '',
    escapeCsvField(entry.effects?.join(';') ?? ''),
    escapeCsvField(entry.notes ?? ''),
    entry.rating != null ? String(entry.rating) : '',
  ];
  return fields.join(',');
}

/**
 * Generates a CSV string from an array of journal entries.
 * Entries are sorted by consumedAt descending.
 * Dates are ISO 8601, numbers up to 2 decimal places, effects joined by
 * semicolons, nulls represented as empty strings, and fields escaped per RFC 4180.
 */
export function generateJournalCsv(entries: JournalEntry[]): string {
  const sorted = [...entries].sort(
    (a, b) => new Date(b.consumedAt).getTime() - new Date(a.consumedAt).getTime()
  );

  const rows = [CSV_HEADER, ...sorted.map(entryToRow)];
  return rows.join('\r\n');
}

/**
 * Triggers a browser download of CSV content.
 * Prepends a UTF-8 BOM for Excel compatibility and uses a hidden <a> element.
 */
export function downloadCsv(csvContent: string, filename: string): void {
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Returns the export filename using the current date.
 * Format: meditate-journal-export-YYYY-MM-DD.csv
 */
export function getExportFilename(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `meditate-journal-export-${year}-${month}-${day}.csv`;
}
